import { useCallback, useState } from "react";
import type { LayoutBody } from "../../shared/layout";

const MAX_HISTORY = 50;

/** Edit-mode working copy of a layout with undo/redo. Nothing is saved until "Done". */
export function useLayoutDraft() {
  const [state, setState] = useState<{ past: LayoutBody[]; present: LayoutBody | null; future: LayoutBody[] }>({ past: [], present: null, future: [] });

  const begin = useCallback((body: LayoutBody) => setState({ past: [], present: structuredClone(body), future: [] }), []);
  const end = useCallback(() => setState({ past: [], present: null, future: [] }), []);

  const apply = useCallback((fn: (b: LayoutBody) => LayoutBody) => {
    setState((s) => {
      if (!s.present) return s;
      const next = fn(s.present);
      if (JSON.stringify(next) === JSON.stringify(s.present)) return s;
      return { past: [...s.past, s.present].slice(-MAX_HISTORY), present: next, future: [] };
    });
  }, []);

  const undo = useCallback(
    () =>
      setState((s) => {
        const prev = s.past[s.past.length - 1];
        if (!prev || !s.present) return s;
        return { past: s.past.slice(0, -1), present: prev, future: [s.present, ...s.future] };
      }),
    [],
  );

  const redo = useCallback(
    () =>
      setState((s) => {
        const [next, ...rest] = s.future;
        if (!next || !s.present) return s;
        return { past: [...s.past, s.present], present: next, future: rest };
      }),
    [],
  );

  return {
    draft: state.present,
    dirty: state.past.length > 0,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    begin,
    end,
    apply,
    undo,
    redo,
  };
}
